/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useEffect, useRef, useState } from "react";
import _ from "lodash";
import type {
  ColumnDefinition,
  FilterState,
  ColumnFilterInitialState,
  ColumnFilterSettings,
  CellRenderProps,
} from "@/types/ColumnTypes";
import {
  bucketTypeConstant,
  supportedGroupTypeConstant,
} from "@/components/SntTableViewCard/SntTableConfigurationConstant";
import SntLink from "@/components/ReactBootstrap/SntLink";
import { getGroupColumnData } from "@/cleanup/utils/tableUtils";
import moment from "@/constants/SensolusMoment";
import GlobalUtils from "@/utils/GlobalUtils";
import { translate } from "@/hooks/useLanguage";
import { LanguageKey } from "@/types/generated/LanguageKey";

/**
 * Custom hook for managing data filtering, pagination, and sorting.
 *
 * @param {Array} allColumnDefs - The definition of all available columns.
 * @param {Array} defaultVisibleColumnKeys - The keys of the columns to be shown and ordered.
 * @param {Object} initialState - Optional initial state for the filter.
 * @returns {Object} - An object containing filter state and functions to interact with it.
 */
export const useColumnFilter = (
  allColumnDefs: ColumnDefinition[] | null,
  initialState: ColumnFilterInitialState = {},
  setting?: ColumnFilterSettings
) => {
  const [filter, setFilter] = useState<FilterState | null>(null);
  

  const [initState, setInitState] = useState<ColumnFilterInitialState | null>(null);
  const [allColumns, setAllColumns] = useState<ColumnDefinition[] | null>(null);

  const [pageCount, setPageCount] = useState(1);
  const [totalItems, setTotalItems] = useState({
    countByFilter: 0,
  });

  const allColumnsByKeyRef = useRef<Record<string, ColumnDefinition>>({});
  const immovableColumnsRef = useRef<ColumnDefinition[]>([]);
  const defaultColumnsRef = useRef<ColumnDefinition[]>([]);

  const allColumnsByKey = allColumnsByKeyRef.current;
  const immovableColumns = immovableColumnsRef.current;
  const defaultColumns = defaultColumnsRef.current;
  const {
    defaultVisibleColumnKeys,
    defaultPageSize = 0,
    defaultSortBy,
    onCountClicked,
    showAllVisibleCols = false,
  } = initialState;

  useEffect(() => {
    if (!allColumnDefs) return;
    const allColumnTransform = _transformColumns(allColumnDefs);
    allColumnsByKeyRef.current = _.keyBy(
      allColumnTransform,
      (o: ColumnDefinition) => o.columnKey
    ) as Record<string, ColumnDefinition>;
    immovableColumnsRef.current = allColumnTransform
      .filter((c: ColumnDefinition) => c.immovable);
    setAllColumns(allColumnTransform);
  }, [allColumnDefs]);

  /**
   * Moves the column flagged `primaryIdentifier` to the front (STIC-15806 R2):
   * a row is recognised by the entity's name, so the name leads the row. Only
   * ever applied to a DEFAULT column set — a column order the user saved is
   * theirs and is passed through untouched. Mirrors `immovable`, which pins
   * the actions column to the back.
   */
  const withPrimaryIdentifierFirst = (columnKeys: string[] | undefined) => {
    if (!columnKeys || columnKeys.length < 2) return columnKeys;
    const primaryKey = columnKeys.find(
      (key) => allColumnsByKeyRef.current[key]?.primaryIdentifier
    );
    if (!primaryKey || columnKeys[0] === primaryKey) return columnKeys;
    return [primaryKey, ...columnKeys.filter((key) => key !== primaryKey)];
  };

  useEffect(() => {
    if (!allColumns) return;
    // When showAllVisibleCols is true and no columns specified, use all available columns
    const effectiveKeys =
      showAllVisibleCols &&
      (!defaultVisibleColumnKeys || defaultVisibleColumnKeys.length === 0)
        ? allColumns.filter((col) => !col.immovable).map((col) => col.columnKey)
        : defaultVisibleColumnKeys;
    defaultColumnsRef.current = getOrderedColumns(
      withPrimaryIdentifierFirst(effectiveKeys) || []
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allColumns]);

  useEffect(() => {
    if (initState) return;

    // Wait for allColumns: which column is the primary identifier is only known
    // once they are loaded, and initState is fixed after the first run.
    if (!allColumns) {
      return;
    }

    // When showAllVisibleCols is true and no columns specified, use all available columns
    const getEffectiveVisibleCols = (specifiedCols: string[] | undefined) => {
      if (
        showAllVisibleCols &&
        (!specifiedCols || specifiedCols.length === 0) &&
        allColumns
      ) {
        return allColumns
          .filter((col) => !col.immovable)
          .map((col) => col.columnKey);
      }
      return specifiedCols;
    };

    if (_.isNil(setting)) {
      setInitState({
        defaultVisibleColumnKeys: withPrimaryIdentifierFirst(
          getEffectiveVisibleCols(defaultVisibleColumnKeys)
        ),
        pageSize: defaultPageSize,
      });
    } else if (!_.isEmpty(setting) && setting) {
      setInitState({
        defaultVisibleColumnKeys:
          setting.columnVisibleInOrder &&
          (setting.columnVisibleInOrder as string[]).length !== 0
            ? setting.columnVisibleInOrder as string[]
            : withPrimaryIdentifierFirst(
                getEffectiveVisibleCols(defaultVisibleColumnKeys)
              ),
        pageSize: (setting.pageSize || defaultPageSize) as number,
        pageIndex: setting.pageIndex as number | undefined,
        orderData: setting.orderData as [string, string] | undefined,
        groups: setting.groups as Array<{
          columnKey: string;
          aggregationType?: string;
          aggregateColumn?: string;
          bucketType?: string;
          bucketConfig?: unknown;
        }> | undefined,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [setting, initState, allColumns]);

  useEffect(() => {
    if (!allColumns || !initState || filter) return;

    const columns = getOrderedColumns(initState.defaultVisibleColumnKeys || []);

    metricColumnsRef.current = [];
    let groupCol;
    if (initState.groups && initState.groups.length) {
      const aggregateColumn = initState.groups[0].aggregateColumn; // this is come by UI settings, it is columnKey
      const bucketType = initState.groups[0].bucketType;
      const bucketConfig = initState.groups[0].bucketConfig;

      groupCol = columns.find((column) => column.columnKey === aggregateColumn) || null;

      if (groupCol) {
        groupCol.isGrouped = true;
        groupCol.bucketType = bucketType;
        groupCol.bucketConfig = bucketConfig;

        const uiSettingMetrics = initState.groups.filter(
          (metricObj) => metricObj.aggregationType
        );

        if (uiSettingMetrics.length === 0) {
          // add count metric as default
          metricColumnsRef.current = [
            getAggregateMetricsColumn(
              groupCol.columnKey + ".count",
              groupCol,
              translate
            ),
          ];
        } else {
          metricColumnsRef.current = uiSettingMetrics
            .map((metricObj) => {
              const meticColumn = columns.find(
                (column) => column.columnKey === metricObj.aggregateColumn
              );
              if (!meticColumn) return null;
              return getAggregateMetricsColumn(
                meticColumn.columnKey +
                  "." +
                  (metricObj.aggregationType || 'count').toLowerCase(),
                meticColumn,
                translate
              );
            })
            .filter((item: ColumnDefinition | null) => item) as ColumnDefinition[];
        }

        columns.push(...metricColumnsRef.current);
      }
    }

    let sortBy = null;
    if (initState.orderData && initState.orderData.length === 2) {
      const orderKey = initState.orderData[0],
        desc =
          initState.orderData[1] === "desc" || initState.orderData[1] === "DESC"
            ? true
            : false;

      if (groupCol) {
        if (groupCol.columnKey === orderKey) {
          sortBy = {
            column: groupCol,
            desc: desc,
            visible: true,
          };
        } else {
          const metricColumn = findMetricColumn(orderKey);
          if (metricColumn) {
            sortBy = {
              column: metricColumn,
              desc: desc,
              visible: true,
            };
          }
        }
      } else {
        const orderColumn = columns.find((column) => column.key === orderKey) || null;
        if (orderColumn) {
          sortBy = {
            column: orderColumn,
            desc: desc,
            visible: true,
          };
        }
      }
    }

    if (!sortBy) {
      // get default sortBy
      if (groupCol) {
        sortBy = {
          column: groupCol,
          desc: false,
          visible: true,
        };
      } else {
        let sortColumn = null,
          desc;

        if (defaultSortBy?.length) {
          sortColumn = defaultColumnsRef.current.find(
            (item) => item.key === defaultSortBy[0]
          );
          desc = defaultSortBy[1];
        }
        if (!sortColumn) {
          sortColumn = defaultColumnsRef.current[0];
          desc = defaultColumnsRef.current[0].desc;
        }

        sortBy = {
          column: sortColumn,
          desc: desc || false,
          visible: true,
        };
      }
    }

    const newFilter: FilterState = {
      pageSize: initState.pageSize || 25,
      pageIndex: initState.pageIndex || 0,
      sortBy: sortBy || { column: columns[0], desc: false, visible: true },
      columns: columns as ColumnDefinition[],
    };
    setFilter(newFilter);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allColumns, initState, filter]);

  const getOrderedColumns = (columnKeys: string[], isOrdered = false): ColumnDefinition[] => {
    // sort by columnKeys and add immovables at the back !
    const keys = Array.from(new Set(columnKeys));
    const columns = keys
      .map((key: string) => {
        const found =
          allColumnsByKey[key] && !allColumnsByKey[key].immovable
            ? allColumnsByKey[key]
            : null;
        return found;
      })
      .filter((n): n is ColumnDefinition => n !== null);

    if (isOrdered) {
      columns.sort((col1, col2) => {
        return (col1.sortedIndex ?? 0) - (col2.sortedIndex ?? 0);
      });
    }

    return columns.concat(immovableColumns);
  };

  const getAggregateMetricsColumn = (
    key: string,
    groupCol: ColumnDefinition,
    translate: (key: LanguageKey) => string
  ): ColumnDefinition => {
    // Remove isGrouped, bucketType, bucketConfig from groupCol to avoid polluting metric columns
     
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const { isGrouped: _isGrouped, bucketType: _bucketType, bucketConfig: _bucketConfig, ...groupColWithoutGroupProps } = groupCol;

    const column = {
      ...groupColWithoutGroupProps,
      columnGroupKey: groupCol.columnKey,
      columnKey: key,
      sortKey: key,
      disabledGroupColumn: true,
      id: key,
      key: key,
      filterKeys: [],
    };
    if (key.includes(".count")) {
      return {
        ...column,
        Header: "Count: " + column.Header,
        description: translate(LanguageKey.group_count_description_key),
        aggregationType: "COUNT",
        Cell: ({ cell }: CellRenderProps) => {
          return countColumnRendering(
            cell.row.original,
            column,
            groupCol.bucketType || '',
            groupCol.bucketConfig
          );
        },
      };
    } else if (key.includes(".min")) {
      return {
        ...column,
        Header: "Min: " + column.Header,
        description: "Min",
        aggregationType: "MIN",
        Cell: ({ cell }: CellRenderProps) => {
          const aliasName = column.aliasName;
          const rs = cell.row.original[aliasName + ".min"];

          const supportedGroupType = (column as any).tableDescriptor?.supportedGroupType;
          if (supportedGroupType === supportedGroupTypeConstant.DATE_BUCKET) {
            return <>{moment(rs as any).format("lll")}</>;
          }

          if ((column as any).durationUnit && !isNaN(rs as number)) {
            return (
              <>
                {GlobalUtils.formatDurationWithUnit(
                  rs,
                  (column as any).durationUnit,
                  translate
                )}
              </>
            );
          }

          return <>{rs}</>;
        },
      };
    } else if (key.includes(".max")) {
      return {
        ...column,
        Header: "Max: " + column.Header,
        description: "Max",
        aggregationType: "MAX",
        Cell: ({ cell }: CellRenderProps) => {
          const aliasName = column.aliasName;
          const rs = cell.row.original[aliasName + ".max"];

          const supportedGroupType = (column as any).tableDescriptor?.supportedGroupType;
          if (supportedGroupType === supportedGroupTypeConstant.DATE_BUCKET) {
            return <>{moment(rs as any).format("lll")}</>;
          }

          if ((column as any).durationUnit && !isNaN(rs as number)) {
            return (
              <>
                {GlobalUtils.formatDurationWithUnit(
                  rs,
                  (column as any).durationUnit,
                  translate
                )}
              </>
            );
          }

          return <>{rs}</>;
        },
      };
    } else if (key.includes(".avg")) {
      return {
        ...column,
        Header: "AVG: " + column.Header,
        description: "AVG",
        aggregationType: "AVG",
        Cell: ({ cell }: CellRenderProps) => {
          const aliasName = column.aliasName;
          const rs = cell.row.original[aliasName + ".avg"];

          if ((column as any).durationUnit && !isNaN(rs as number)) {
            return (
              <>
                {GlobalUtils.formatDurationWithUnit(
                  rs,
                  (column as any).durationUnit,
                  translate
                )}
              </>
            );
          }

          return <>{rs}</>;
        },
      };
    } else if (key.includes(".sum")) {
      return {
        ...column,
        Header: "SUM: " + column.Header,
        description: "SUM",
        aggregationType: "SUM",
        Cell: ({ cell, value }) => {
          const aliasName = column.aliasName;
          const rs = cell.row.original[aliasName + ".sum"];

          if (column.durationUnit && !isNaN(Number(rs))) {
            return (
              <>
                {GlobalUtils.formatDurationWithUnit(
                  Number(rs),
                  column.durationUnit,
                  translate
                )}
              </>
            );
          }

          return <>{rs}</>;
        },
      };
    } else {
      return column;
    }
  };

  const countColumnRendering = (
    full: Record<string, unknown>,
    column: ColumnDefinition,
    bucketType: string,
    bucketConfig: unknown
  ): React.ReactNode => {
    const aliasName = column.aliasName;
    if (
      onCountClicked &&
      column.redirectFilter &&
      aliasName &&
      full[aliasName] &&
      full[aliasName] !== '{"id": null, "name": null}'
    ) {
      return (
        <SntLink
          onClick={() => {
            onCountClicked(
              column.redirectFilter || '',
              full[aliasName] as string,
              bucketType,
              bucketConfig
            );
          }}
        >
          {String(full[aliasName + ".count"] || 0)}
        </SntLink>
      );
    }

    return aliasName ? <>{full[aliasName + ".count"] || 0}</> : <>0</>;
  };

  const loadData = () => {
    if (!filter) return;
    setPaging(filter.pageIndex, filter.pageSize);
    setPageCount(1);
    _updateFilter({});
  };

  const resetDefault = () => {
    if (!filter || defaultColumns.length === 0) return;
    setPaging(0, filter.pageSize);
    setSortByKey(defaultColumns[0].key, false);
  };

  const refresh = () => {
    _updateFilter({});
  };

  const setVisibleColumnKeys = (visibleColumns: string[], isOrdered = false) => {
    if (!filter) return;
    let newColumns: ColumnDefinition[] = [];
    if (visibleColumns && visibleColumns.length > 0) {
      newColumns = getOrderedColumns(visibleColumns, isOrdered);
    }
    if (!newColumns.length) {
      newColumns = defaultColumns;
    }

    const columns = filter.columns.filter((col) => !col.aggregationType);
    newColumns = newColumns.map((item) => {
      const rs = { ...item };
      delete rs.isGrouped;
      return rs;
    });
    if (!_.isEqual(columns, newColumns)) {
      _updateFilter({ columns: newColumns });
    }
  };

  const setPaging = (pageIndex: number, pageSize: number) => {
    if (!filter) return;
    if (pageIndex !== filter.pageIndex || pageSize !== filter.pageSize) {
      _updateFilter({ pageSize: pageSize, pageIndex: pageIndex });
    }
  };

  const setSortByKey = (key: string, desc: boolean) => {
    if (!filter) return;
    let sortColumn: ColumnDefinition | undefined = allColumnsByKey[key];
    if (!sortColumn) {
      sortColumn = findMetricColumn(key);
    }
    if (sortColumn && _sortChanged(sortColumn, desc)) {
      _updateFilter({
        sortBy: {
          column: sortColumn,
          desc: desc,
          visible: _isVisible(sortColumn),
        },
        pageIndex: 0,
      });
    }
  };

  // const setFilterField = (name, value) => {
  //   if (!_.isEqual(value, filter[name])) {
  //     _updateFilter({ [name]: value });
  //   }
  // };

  const _sortChanged = (column: ColumnDefinition, desc: boolean) => {
    if (!filter) return false;
    return (
      !_.isEqual(column, filter.sortBy.column) || desc !== filter.sortBy.desc
    );
  };

  const _isVisible = (column: ColumnDefinition) => {
    if (!filter?.columns) return false;
    return (
      filter.columns.filter((c) => c.columnKey === column.columnKey).length > 0
    );
  };

  const _updateFilter = (props: Partial<FilterState>) => {
    console.log("_updateFilter called with props:", props);
    setFilter((prevFilter) => {
      if (!prevFilter) return prevFilter;
      const value = {
        ...prevFilter,
        ...props,
      };
      console.log(
        "_updateFilter - new filter state columns:",
        value.columns?.map((c) => ({
          key: c.key,
          isGrouped: c.isGrouped,
          aggregationType: c.aggregationType,
        }))
      );
      return value;
    });
  };

  const _transformColumns = (columns: ColumnDefinition[]) => {
    return columns.map((column) => {
      return {
        ...column,
        sortedIndex: column.sortedIndex,
        categoryId: column.categoryId || "",
        category: column.category || "",
        columnKey: column.key,
        supportKibanaKey: column.supportKibanaKey,
        supportedGroupType: column.supportedGroupType,
        supportedAggregateMetrics: column.supportedAggregateMetrics,
        supportedGroupUnits: column.supportedGroupUnits,
        filterKeys: column.filterKeys,
        cellCopyData: column.cellCopyData,
        cellFilterValue: column.cellFilterValue,
        aggregateColumn: column.aggregateColumn,
        aliasName: column.aliasName,
        redirectFilter: column.redirectFilter,
        disabledGroupColumn: column.disabledGroupColumn,
        sortKey: column.sortKey || column.key,
        description: column.description ? column.description : "",
        title: column.title,
        accessor: column.key,
        Header: column.title || "",
        systemOnly: column.systemOnly,
        // Check both column.style.width and column.width for extraCols compatibility
        width: column.style?.width || column.width || 150,
        minWidth: column.style?.minWidth || column.minWidth || 150,
        maxWidth: column.style?.maxWidth || column.maxWidth || 600,
        style: column.style,
      };
    });
  };

  function getPageData() {
    if (!filter) return {};
    let sort = null;
    if (filter.sortBy) {
      sort = `[{"property": "${filter.sortBy.column.sortKey}", "direction": "${
        filter.sortBy.desc ? "DESC" : "ASC"
      }"}]`;
    }

    const columns = filter.columns.filter((c) => c.key !== "actions");
    const { groupColumn, groups } = getGroupColumnData(columns);

    if (groupColumn) {
      return {
        start: filter.pageSize * filter.pageIndex,
        limit: filter.pageSize,
        sort: sort,
        isGrouped: true,
        groups: groups, // use in tableSetting for save filter
        columns: columns.map((column) => {
          const findGroup = groups.find(
            (item) => item.columnKey === column.columnKey
          );
          if (findGroup) {
            return {
              ...findGroup,
              isGrouped: column.isGrouped || null,
              order:
                filter.sortBy.column.columnKey === column.columnKey
                  ? filter.sortBy.desc
                    ? "DESC"
                    : "ASC"
                  : null,
            };
          }

          return {
            columnKey: column.columnKey,
            isGrouped: column.isGrouped || null,
            order: null,
          };
        }),
        requestedTableColumns: groups.map((column) => {
          return {
            columnKey: column.columnGroupKey,
            bucketType: column.aggregationType ? null : column.bucketType,
            bucketConfig: column.aggregationType ? null : column.bucketConfig,
            aggregationType: column.aggregationType,
            order:
              filter.sortBy.column.columnKey === column.columnKey
                ? filter.sortBy.desc
                  ? "DESC"
                  : "ASC"
                : null,
          };
        }),
      };
    }

    return {
      start: filter.pageSize * filter.pageIndex,
      limit: filter.pageSize,
      sort: sort,
      columns: columns.map((column) => {
        return {
          columnKey: column.columnKey,
          isGrouped: column.isGrouped || null,
          order:
            filter.sortBy.column.columnKey === column.columnKey
              ? filter.sortBy.desc
                ? "DESC"
                : "ASC"
              : null,
        };
      }),
      requestedTableColumns: columns.map((column) => column.key),
    };
  }
  const metricColumnsRef = useRef<ColumnDefinition[]>([]);

  const toggleGroupColumnKey = (columnKey: string, isForceGroup: boolean) => {
    if (!filter) return;
    // Filter out metric columns but keep all regular columns including their group state
    const columns = filter.columns
      .filter((column) => !column.aggregationType)
      .map((column) => {
        // Create a shallow copy to avoid mutating the original filter.columns
        const col = { ...column };
        // Only clear isGrouped if isForceGroup is true (for backwards compatibility)
        if (isForceGroup) delete col.isGrouped;
        return col;
      });

    let firstCol = columns.find((column) => column.key === columnKey);
    // The following grouping logic relies on the user clicking a column header to initiate the grouping.
    // The grouped column may become undefined if this grouping action is used at another place
    if (!firstCol) {
      firstCol = allColumnsByKey[columnKey];
      columns.push(firstCol);
    }

    // Toggle the group state of this specific column
    const wasGrouped = firstCol.isGrouped;
    firstCol.isGrouped = !firstCol.isGrouped;

    // needed for mobile card checking false/true/null
    if (!firstCol.isGrouped) {
      delete firstCol.isGrouped;
      delete firstCol.bucketType;
      delete firstCol.bucketConfig;
    }

    // Get all currently grouped columns
    const groupedColumns = columns.filter((c) => c.isGrouped);

    console.log("toggleGroupColumnKey - columnKey:", columnKey);
    console.log(
      "toggleGroupColumnKey - firstCol.isGrouped:",
      firstCol.isGrouped
    );
    console.log(
      "toggleGroupColumnKey - groupedColumns:",
      groupedColumns.map((c) => ({ key: c.key, isGrouped: c.isGrouped }))
    );

    // Set sortBy to the first grouped column, or the toggled column if it's now grouped
    const sortBy = filter.sortBy;
    if (firstCol.isGrouped) {
      sortBy.column = firstCol;
    } else if (groupedColumns.length > 0) {
      sortBy.column = groupedColumns[0];
    } else {
      sortBy.column = firstCol;
    }

    // Collect all metric columns from all grouped columns
    metricColumnsRef.current = [];
    groupedColumns.forEach((groupedCol) => {
      // Set bucket type for newly grouped columns
      if (groupedCol.key === columnKey && !wasGrouped) {
        const bucketType =
          groupedCol.supportedGroupType ===
          supportedGroupTypeConstant.DATE_BUCKET
            ? bucketTypeConstant.DAY
            : groupedCol.supportedGroupType ===
              supportedGroupTypeConstant.NUMERIC_BUCKET
            ? bucketTypeConstant.AUTO_WIDTH
            : groupedCol.supportedGroupType ===
              supportedGroupTypeConstant.TAG_BASED
            ? bucketTypeConstant.TAGS_FULL
            : null;
        groupedCol.bucketType = bucketType as string;
        groupedCol.bucketConfig = null;
      }

      // Add metric columns for this grouped column
      if (groupedCol.supportedAggregateMetrics) {
        const metrics = groupedCol.supportedAggregateMetrics.map((metric) =>
          getAggregateMetricsColumn(
            groupedCol.key + "." + metric.toLowerCase(),
            groupedCol,
            translate
          )
        );
        metricColumnsRef.current.push(...metrics);
      }
    });

    console.log(
      "toggleGroupColumnKey - updating filter with columns:",
      [...columns, ...metricColumnsRef.current].map((c) => ({
        key: c.key,
        isGrouped: c.isGrouped,
        aggregationType: c.aggregationType,
      }))
    );

    _updateFilter({
      columns: [...columns, ...metricColumnsRef.current],
      sortBy: sortBy,
      pageIndex: 0,
    });
  };

  const setColumnsSetting = (
    columnKeys: string[],
    sortBy = { key: "", desc: false },
    groupKeys: string[] | string,
    bucketType: string,
    bucketConfig: unknown,
    aggregateMetrics: string[]
  ) => {
    if (!filter) return;
    let columns: ColumnDefinition[] = [];
    if (columnKeys.length > 0) {
      columns = getOrderedColumns(columnKeys, false);
    }
    if (!columns.length) {
      columns = defaultColumns;
    }

    columns.map((column) => {
      delete column.isGrouped;
      delete column.bucketType;
      delete column.bucketConfig;
      return column;
    });

    const sort = filter.sortBy;

    metricColumnsRef.current = [];

    // Support both array and single string for backward compatibility
    const groupKeysArray = Array.isArray(groupKeys)
      ? groupKeys
      : groupKeys
      ? [groupKeys]
      : [];

    if (groupKeysArray.length > 0) {
      // Mark all group columns as grouped
      groupKeysArray.forEach((groupKey) => {
        const groupCol = columns.find((column) => column.key === groupKey);
        if (groupCol) {
          groupCol.isGrouped = true;
          groupCol.bucketType = (
            groupCol.supportedGroupType ===
            supportedGroupTypeConstant.DATE_BUCKET
              ? bucketType || bucketTypeConstant.DAY
              : groupCol.supportedGroupType ===
                supportedGroupTypeConstant.NUMERIC_BUCKET
              ? bucketType || bucketTypeConstant.AUTO_WIDTH
              : groupCol.supportedGroupType ===
                supportedGroupTypeConstant.TAG_BASED
              ? bucketType || bucketTypeConstant.TAGS_FULL
              : null
          ) as string | undefined;
          groupCol.bucketConfig =
            groupCol.supportedGroupType ===
              supportedGroupTypeConstant.NUMERIC_BUCKET &&
            bucketType === bucketTypeConstant.WIDTH
              ? bucketConfig
              : null;
        }
      });

      // Use the first group key for metrics if no specific metrics provided
      const firstGroupKey = groupKeysArray[0];
      const metrics =
        aggregateMetrics?.length > 0
          ? aggregateMetrics
          : [firstGroupKey + ".count"];
      metricColumnsRef.current = metrics.map((metric) => {
        const metricColumnKey = metric.split(".")[0];
        const metricColumn = columns.find(
          (column) => column.key === metricColumnKey
        );
        return getAggregateMetricsColumn(metric, metricColumn || columns[0], translate);
      });

      // Check if sort column is one of the group columns
      const groupCol = columns.find(
        (column) =>
          groupKeysArray.includes(column.key) && column.key === sortBy.key
      );
      if (groupCol) {
        sort.column = groupCol;
      } else {
        const metricColumn = findMetricColumn(sortBy.key);
        sort.column = metricColumn || metricColumnsRef.current[0];
      }
      sort.desc = sortBy.desc || false;
    } else {
      const sortColumn = columns.find((column) => column.key === sortBy.key);
      sort.column = sortColumn || columns[0];
      sort.desc = sortBy.desc || false; // sort.desc does not accept undefined or null value
    }

    _updateFilter({
      columns: [...columns, ...metricColumnsRef.current],
      sortBy: { ...sort },
      pageIndex: 0,
    });
  };

  const findMetricColumn = (key: string): ColumnDefinition | undefined => {
    return metricColumnsRef.current.find((column) => column.key === key);
  };

  const removeColumnKey = (columnKey: string) => {
    if (!filter) return;
    const columns = filter.columns.filter((column) => column.key !== columnKey);
    _updateFilter({ columns: columns });
  };

  if (!filter) return {};
  return {
    filter,
    allColumns,
    getPageData,
    setPaging,
    pageCount,
    setPageCount,
    setSortByKey,
    // setFilterField,
    setVisibleColumnKeys,
    refresh,
    loadData,
    resetDefault,
    totalItems,
    setTotalItems,
    removeColumnKey,
    toggleGroupColumnKey,
    defaultVisibleColumnKeys,
    setColumnsSetting,
  };
};
